'use client';

import {
	Box,
	Button,
	Center,
	FileUploadHiddenInput,
	IconButton,
	useFileUploadContext,
	type BoxProps,
} from '@chakra-ui/react'
import { Controller, useFormContext } from 'react-hook-form'
import type { FormImageUploadInputProps, FormType } from '../../types/types'
import { LuX } from 'react-icons/lu'
import React, { useState } from 'react'
import { FaRegImages } from "react-icons/fa6";

import type { FileChangeDetails } from '@zag-js/file-upload'
import { FileUploadRoot } from '../../../components'
import { FormField } from '../FormField/FormField'
import { NextImageWithSkeleton } from '../../../custom'

type ImagePreviewProps = BoxProps & {
	previewUrl: string | undefined
	handleClear: () => void
}

const ImagePreview = ({ previewUrl, handleClear, ...props }: ImagePreviewProps) => {
	const { acceptedFiles, openFilePicker, deleteFile } = useFileUploadContext()

	const handleDeleteFile = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault()
		e.stopPropagation()
		if (acceptedFiles[0]) {
			deleteFile(acceptedFiles[0])
		}

		if (previewUrl) {
			handleClear()
		}
	}


	return (
		<Box
			border={'2px dashed'} borderColor={'gray.200'} borderRadius={'lg'}
			cursor={'pointer'}
			pos={'relative'}
			onClick={openFilePicker}
			p={1}
			{...props}
		>
			{previewUrl ? (
				<React.Fragment>
					<NextImageWithSkeleton
            fill
						alt={previewUrl}
						objectFit={'contain'}
						src={previewUrl}
            height='100%'
            width='100%'
					/>
					<IconButton
						opacity={0.7}
						position={'absolute'}
						right={'0'}
						size={'xs'}
						top={'0'}
						onClick={handleDeleteFile}
					>
						<LuX />
					</IconButton>
				</React.Fragment>
			) : (
				<Center h={'100%'} w={'100%'}>
					<IconButton variant="plain" size="md" color='gray.500'>
						<FaRegImages />
					</IconButton>
				</Center>
			)}
		</Box>
	)
}

export const FormImageUpload = <T extends FormType>({
	name,
	label,
	helperText,
	optionalText,
	isRequired,
	_formField,
	...props
}: FormImageUploadInputProps<T>) => {
	const { control, getValues } = useFormContext()
	const defaultImageUrl = getValues(name) as string | undefined
	const [previewUrl, setPreviewUrl] = useState<string | undefined>(
		defaultImageUrl
	)
	const { setValue } = useFormContext()

	function renderPreviewImage(
		file: File,
		callbackFn: (result: string) => void
	) {
		const reader = new FileReader()
		reader.onloadend = () => callbackFn(reader.result as string)
		reader.readAsDataURL(file)
	}

	const handleFileChange = (detail: FileChangeDetails, onChange: any) => {
		const file = detail.acceptedFiles[0] 
		onChange(file)
		if (file) {
			renderPreviewImage(file, setPreviewUrl)
		} else {
			setPreviewUrl(undefined)
		}
	}

	const handleClear = () =>{ 
		setPreviewUrl(undefined)
		setValue(name, undefined as never)
	}

	return (
		<FormField
			helperText={helperText}
			label={label}
			name={name}
			optionalText={optionalText}
			required={isRequired}
			{..._formField}
		>
			<Controller
				control={control}
				name={name}
				render={({ field }) => (
					<FileUploadRoot
            maxFiles={1}
						onFileChange={(detail) => handleFileChange(detail, field.onChange)}
					>
						<FileUploadHiddenInput />
						<ImagePreview previewUrl={previewUrl} handleClear={handleClear} {...props} />
					</FileUploadRoot>
				)}
			/>
		</FormField>
	)
}
